import { detectColumnType, TYPES } from './typeDetector.js';

const CLOSED_DOMAIN_LIMIT = 50;   // sostre absolut de valors distints d'un domini tancat
const CLOSED_DOMAIN_RATIO = 0.5;  // únics/informats: tancat només si els valors ES REPETEIXEN
const MIN_SAMPLE = 15;            // per sota, la mostra és massa petita per decidir
const OPEN_SAMPLE_LIMIT = 100;
const ROW_BATCH = 5000;          // files per tongada abans de cedir el thread (variant async)

// Cedeix el thread al navegador (un macrotask) perquè pugui repintar el loading.
const nextTask = () => new Promise((resolve) => setTimeout(resolve));

function isNullish(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
}

function normalizeForKey(value) {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// Deriva les estadístiques finals d'una columna a partir dels acumuladors (valors,
// informats, freqüències). Compartit entre la variant síncrona i l'asíncrona perquè
// el resultat sigui idèntic.
function finalizeColumn(name, idx, values, informed, freq, totalRecords) {
  const detectedType = detectColumnType(values);
  const uniqueCount = freq.size;
  // Classificació de domini en 3 estats (NOMÉS a partir del fitxer, no del formulari):
  //   · 'empty'        — cap valor informat.
  //   · 'open'         — obert per naturalesa (email) o alta cardinalitat (no repeteixen).
  //   · 'closed'       — conjunt petit de valors que ES REPETEIXEN (categòric).
  //   · 'undetermined' — mostra massa petita per decidir (pocs registres informats).
  let domainClass;
  if (informed === 0) {
    domainClass = 'empty';
  } else if (detectedType === TYPES.EMAIL) {
    domainClass = 'open';
  } else if (informed < MIN_SAMPLE) {
    domainClass = 'undetermined';
  } else if (uniqueCount <= CLOSED_DOMAIN_LIMIT && uniqueCount / informed <= CLOSED_DOMAIN_RATIO) {
    domainClass = 'closed';
  } else {
    domainClass = 'open';
  }
  const isClosedDomain = domainClass === 'closed';
  // Mostra completa quan és tancat o indeterminat (pocs valors igualment); top-N si obert.
  const keepAll = isClosedDomain || domainClass === 'undetermined';
  const sortedEntries = [...freq.entries()].sort((a, b) => b[1] - a[1]);
  const slice = keepAll ? sortedEntries : sortedEntries.slice(0, OPEN_SAMPLE_LIMIT);
  const sample = slice.map(([value, count]) => ({ value, count }));

  return {
    name,
    index: idx,
    detectedType,
    totalRecords,
    informed,
    nulls: totalRecords - informed,
    nullRatio: totalRecords === 0 ? 0 : (totalRecords - informed) / totalRecords,
    uniqueCount,
    closedDomain: isClosedDomain,
    domainClass,
    sample,
  };
}

export function computeColumnStats(headers, rows) {
  const totalRecords = rows.length;
  const stats = headers.map((name, idx) => {
    const values = new Array(totalRecords);
    let informed = 0;
    const freq = new Map();

    for (let r = 0; r < totalRecords; r++) {
      const v = rows[r]?.[idx];
      values[r] = v;
      if (!isNullish(v)) {
        informed += 1;
        const key = normalizeForKey(v);
        freq.set(key, (freq.get(key) || 0) + 1);
      }
    }

    return finalizeColumn(name, idx, values, informed, freq, totalRecords);
  });

  return {
    totalRecords,
    totalFields: headers.length,
    typeBreakdown: summarizeTypes(stats),
    columns: stats,
  };
}

// Variant asíncrona, troçejada: fa un sol pas per les files en tongades, cedint el
// thread entre tongades (perquè el navegador no es bloquegi en fitxers molt grans) i
// emetent progrés via onProgress({ phase:'fields', processed, total }). El resultat és
// IDÈNTIC a computeColumnStats (mateixos acumuladors i finalització per columna).
export async function computeColumnStatsAsync(headers, rows, onProgress = () => {}) {
  const totalRecords = rows.length;
  const nCols = headers.length;
  const values = headers.map(() => new Array(totalRecords));
  const informed = new Array(nCols).fill(0);
  const freq = headers.map(() => new Map());

  for (let start = 0; start < totalRecords; start += ROW_BATCH) {
    const end = Math.min(start + ROW_BATCH, totalRecords);
    for (let r = start; r < end; r++) {
      const row = rows[r];
      for (let c = 0; c < nCols; c++) {
        const v = row?.[c];
        values[c][r] = v;
        if (!isNullish(v)) {
          informed[c] += 1;
          const key = normalizeForKey(v);
          freq[c].set(key, (freq[c].get(key) || 0) + 1);
        }
      }
    }
    onProgress({ phase: 'fields', processed: end, total: totalRecords });
    if (end < totalRecords) await nextTask();
  }

  const stats = [];
  for (let c = 0; c < nCols; c++) {
    stats.push(finalizeColumn(headers[c], c, values[c], informed[c], freq[c], totalRecords));
    if ((c & 7) === 0) await nextTask(); // cedeix cada 8 columnes (detecció de tipus + sort)
  }

  return {
    totalRecords,
    totalFields: nCols,
    typeBreakdown: summarizeTypes(stats),
    columns: stats,
  };
}

function summarizeTypes(columns) {
  const counts = {};
  for (const c of columns) {
    counts[c.detectedType] = (counts[c.detectedType] || 0) + 1;
  }
  return counts;
}

export { TYPES };
